import { formatIsoDate } from "@/components/availability/date-helpers";
import type { TWorkspaceCapacityGroupBy } from "@/types/workspace-capacity";
import { DAY_PX, MIN_COLUMNS, PERIOD_DAYS } from "./capacity-planning-constants";

const DAY_MS = 86_400_000;

export function parseIsoDate(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

/** Whole calendar days from `a` to `b` (DST-safe). */
function daysBetween(a: Date, b: Date): number {
  const ad = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bd = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((bd - ad) / DAY_MS);
}

/** Monday-anchored start of week. */
function startOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Snap a date to the start of the period (per zoom) that contains it. */
export function periodStart(zoom: TWorkspaceCapacityGroupBy, date: Date): Date {
  if (zoom === "day") return startOfWeek(date);
  if (zoom === "week") return startOfWeek(date);
  return startOfMonth(date);
}

/** Inclusive day-count of a `columns`-wide window anchored at `viewStart`. */
function windowDays(zoom: TWorkspaceCapacityGroupBy, viewStart: Date, columns: number): number {
  if (zoom === "day" || zoom === "week") return columns * PERIOD_DAYS[zoom];
  // month → that many whole calendar months from the anchor month.
  return daysBetween(viewStart, addMonths(viewStart, columns));
}

/**
 * Where a window of `zoom` anchored on `date` starts.
 *
 * Week and month windows are calendar-aligned, so they snap to the period
 * containing the anchor — a week column that did not start on a Monday would
 * not be a week. A **day** window is not: its arrows step one day at a time
 * (NG QA, 2026-09-14), so re-snapping it here would undo every step and pin the
 * view to the anchor's Monday.
 */
export function windowStart(zoom: TWorkspaceCapacityGroupBy, date: Date): Date {
  if (zoom === "day") return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return periodStart(zoom, date);
}

export interface CapacityWindow {
  zoom: TWorkspaceCapacityGroupBy;
  viewStart: Date;
  /** Inclusive last day of the window. */
  viewEnd: Date;
  visibleDays: number;
  dayPx: number;
  canvasWidth: number;
  /** ISO date of the first day (for API start_date). */
  startIso: string;
  /** ISO date of the last day (for API end_date). */
  endIso: string;
}

/**
 * Build the visible window for a zoom anchored so the anchor's period is
 * leftmost, `columns` periods wide. The count is the viewport's to decide (see
 * `capacityColumnsForWidth`); it defaults to the zoom's minimum so a caller with
 * nothing measured yet still gets a usable window.
 */
export function buildWindow(
  zoom: TWorkspaceCapacityGroupBy,
  anchor: Date,
  columns: number = zoom === "month" ? 3 : MIN_COLUMNS[zoom],
): CapacityWindow {
  const viewStart = windowStart(zoom, anchor);
  const visibleDays = windowDays(zoom, viewStart, Math.max(1, Math.floor(columns)));
  const viewEnd = addDays(viewStart, visibleDays - 1);
  const dayPx = DAY_PX[zoom];
  return {
    zoom,
    viewStart,
    viewEnd,
    visibleDays,
    dayPx,
    canvasWidth: dayPx * visibleDays,
    startIso: formatIsoDate(viewStart),
    endIso: formatIsoDate(viewEnd),
  };
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Human-readable inclusive window range, e.g. "Jun 11 – Jul 8, 2026". */
export function formatWindowRange(win: CapacityWindow): string {
  const start = `${MONTH_SHORT[win.viewStart.getMonth()]} ${win.viewStart.getDate()}`;
  const startYear =
    win.viewStart.getFullYear() !== win.viewEnd.getFullYear()
      ? `, ${win.viewStart.getFullYear()}`
      : "";
  const end = `${MONTH_SHORT[win.viewEnd.getMonth()]} ${win.viewEnd.getDate()}, ${win.viewEnd.getFullYear()}`;
  return `${start}${startYear} – ${end}`;
}
